import { cloneDeep, Dictionary, groupBy } from "lodash";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { BenchmarkingCounty } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingCounty";
import { BenchmarkingState } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingState";

export interface StateCountyData {
  counties: NestingParentDropdownItem[];
  rawCounties: BenchmarkingCounty[];
  selectedCounties: string[];
}

// Derives the nested Service Area dropdown items (grouped by CBSA), the flat raw county list, and the
// default "all selected" county-id list for a given state. Mirrors the selectedState effect in
// BenchmarkingFiltersContext, but as a pure function so a flyout can stage its own state selection
// locally without mutating the shared context.
export const buildStateCountyData = (state: BenchmarkingState, countyIdFilter: number[] | null): StateCountyData => {
  const filterSet = countyIdFilter ? new Set(countyIdFilter) : null;
  const sourceCounties: BenchmarkingCounty[] = filterSet
    ? state.counties.filter(x => filterSet.has(x.countyId))
    : state.counties;

  const cloned: BenchmarkingCounty[] = cloneDeep(sourceCounties);
  const mappedCounties: DropdownItem<string>[] = cloned.map(x => {
    return { key: x.countyId.toString(), name: x.countyName }
  });

  const nestedCounties: NestingParentDropdownItem[] = [];
  const groupedByCbsa: Dictionary<BenchmarkingCounty[]> = groupBy(cloned.filter(x => !!x.cbsaName), "cbsaName");

  Object.entries(groupedByCbsa).forEach((datum) => {
    const mappedGroupedCounties: DropdownItem<string>[] = datum[1].map(x => {
      return { key: x.countyId.toString(), name: x.countyName }
    });

    const nestedCounty: NestingParentDropdownItem = {
      checkboxStatus: CheckboxStateEnum.Unchecked,
      children: mappedGroupedCounties,
      grandparentTitle: "Statistical Area",
      key: datum[0],
      name: datum[0],
    }

    nestedCounties.push(nestedCounty);
  });

  const county: NestingParentDropdownItem = {
    checkboxStatus: CheckboxStateEnum.Unchecked,
    children: mappedCounties,
    grandparentTitle: "County",
    hideParent: true,
    key: "county",
    name: "",
  };

  nestedCounties.push(county);

  return {
    counties: nestedCounties,
    rawCounties: sourceCounties,
    selectedCounties: sourceCounties.map(x => x.countyId.toString()),
  };
}
